import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crown,
  Users,
  Heart,
  TrendingUp,
  Sparkles,
  Target,
  Award,
  Zap,
  Shield,
  Brain,
  Smile,
  HandHeart,
  Compass,
  Star,
  Gift,
  Coffee,
  Sun,
  Moon,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Circle,
  CheckCircle2,
  Lock,
  Unlock,
  MessageSquare,
  Bell,
  BarChart3,
  Activity,
  Coins,
  Trophy,
  Flame,
  TreePine,
  Waves,
  Home
} from 'lucide-react';

interface StakeholderMetrics {
  owner: {
    revenue: number;
    growth: number;
    reputation: number;
    sustainability: number;
  };
  team: {
    satisfaction: number;
    productivity: number;
    learning: number;
    wellbeing: number;
  };
  guest: {
    delight: number;
    discovery: number;
    comfort: number;
    connection: number;
  };
}

interface MasteryMoment {
  id: string;
  type: 'owner' | 'team' | 'guest';
  icon: React.ElementType;
  title: string;
  impact: string;
  timestamp: string;
  connected?: string[];
}

export default function UnifiedMastery() {
  const navigate = useNavigate();
  const [selectedView, setSelectedView] = useState<'unified' | 'owner' | 'team' | 'guest'>('unified');
  const [metrics, setMetrics] = useState<StakeholderMetrics>({
    owner: { revenue: 87, growth: 23, reputation: 94, sustainability: 78 },
    team: { satisfaction: 91, productivity: 85, learning: 92, wellbeing: 88 },
    guest: { delight: 96, discovery: 89, comfort: 93, connection: 87 }
  });
  const [harmonyScore, setHarmonyScore] = useState(89);
  const [activeConnections, setActiveConnections] = useState<string[]>([]);

  useEffect(() => {
    // Simulate real-time metric updates
    const interval = setInterval(() => {
      setMetrics(prev => ({
        owner: {
          ...prev.owner,
          revenue: Math.min(100, prev.owner.revenue + Math.random() * 2 - 0.5)
        },
        team: {
          ...prev.team,
          satisfaction: Math.min(100, prev.team.satisfaction + Math.random() * 2 - 0.5)
        },
        guest: {
          ...prev.guest,
          delight: Math.min(100, prev.guest.delight + Math.random() * 2 - 0.5)
        }
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const masteryMoments: MasteryMoment[] = [
    {
      id: '1',
      type: 'guest',
      icon: Heart,
      title: 'Maria discovered the secret beach',
      impact: '+5 Guest Delight → +2 Team Pride',
      timestamp: '2 min ago',
      connected: ['team']
    },
    {
      id: '2',
      type: 'team',
      icon: Trophy,
      title: 'João prevented water shortage',
      impact: '+10 Sustainability → +8 Guest Comfort',
      timestamp: '15 min ago',
      connected: ['owner', 'guest']
    },
    {
      id: '3',
      type: 'owner',
      icon: TrendingUp,
      title: 'Revenue target exceeded',
      impact: '+15% Bonus Pool → +12 Team Satisfaction',
      timestamp: '1 hour ago',
      connected: ['team']
    },
    {
      id: '4',
      type: 'guest',
      icon: Star,
      title: '5-star review from the Smiths',
      impact: '+3 Reputation → +5 Future Bookings',
      timestamp: '2 hours ago',
      connected: ['owner']
    }
  ];

  const ownerGoals = [
    { name: 'Sustainable Growth', progress: 78, target: '20% YoY', icon: TreePine },
    { name: 'Team Retention', progress: 92, target: '95%', icon: Users },
    { name: 'Guest Loyalty', progress: 85, target: '80% Return', icon: Heart },
    { name: 'Carbon Neutral', progress: 65, target: 'By 2025', icon: Waves }
  ];

  const teamChallenges = [
    { name: 'Morning Excellence', completed: true, reward: 'Extra Break', icon: Sun },
    { name: 'Guest Surprises', progress: 3, target: 5, reward: 'Team Lunch', icon: Gift },
    { name: 'Zero Complaints Week', progress: 5, target: 7, reward: 'Bonus Day', icon: Shield },
    { name: 'Innovation Ideas', progress: 2, target: 3, reward: 'Implement One', icon: Brain }
  ];

  const guestJourney = [
    { stage: 'Arrival', mastered: true, delight: 'Personalized welcome drink' },
    { stage: 'First Night', mastered: true, delight: 'Perfect room temperature' },
    { stage: 'Exploration', mastered: false, delight: '3 secrets to discover' },
    { stage: 'Departure', mastered: false, delight: 'Surprise gift awaits' }
  ];

  const renderUnifiedView = () => (
    <div className="space-y-6">
      {/* Harmony Score */}
      <div className="bg-gradient-to-br from-purple-600 via-blue-600 to-green-600 rounded-3xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">Pousada Harmony</h2>
            <p className="text-white/80">When everyone wins, magic happens</p>
          </div>
          <div className="relative w-24 h-24">
            <svg className="transform -rotate-90 w-24 h-24">
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-white/20"
              />
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${harmonyScore * 2.51} 251`}
                className="text-white"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold">{harmonyScore}%</span>
            </div>
          </div>
        </div>

        {/* Three Pillars */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setSelectedView('owner')}
            className="bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-all"
          >
            <Crown className="h-6 w-6 mx-auto mb-1" />
            <p className="text-xs font-medium">Owner</p>
            <p className="text-lg font-bold">{Math.round(metrics.owner.revenue)}%</p>
          </button>
          <button
            onClick={() => setSelectedView('team')}
            className="bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-all"
          >
            <Users className="h-6 w-6 mx-auto mb-1" />
            <p className="text-xs font-medium">Team</p>
            <p className="text-lg font-bold">{Math.round(metrics.team.satisfaction)}%</p>
          </button>
          <button
            onClick={() => setSelectedView('guest')}
            className="bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-all"
          >
            <Heart className="h-6 w-6 mx-auto mb-1" />
            <p className="text-xs font-medium">Guests</p>
            <p className="text-lg font-bold">{Math.round(metrics.guest.delight)}%</p>
          </button>
        </div>
      </div>

      {/* Live Mastery Moments */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-yellow-400" />
          Live Mastery Moments
        </h3>
        <div className="space-y-3">
          {masteryMoments.map((moment) => {
            const Icon = moment.icon;
            return (
              <div key={moment.id} className="bg-gray-700/50 rounded-lg p-3">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    moment.type === 'owner' ? 'bg-purple-500/20' :
                    moment.type === 'team' ? 'bg-blue-500/20' :
                    'bg-green-500/20'
                  }`}>
                    <Icon className={`h-5 w-5 ${
                      moment.type === 'owner' ? 'text-purple-400' :
                      moment.type === 'team' ? 'text-blue-400' :
                      'text-green-400'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{moment.title}</p>
                    <p className="text-gray-400 text-xs mt-1">{moment.impact}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-gray-500 text-xs">{moment.timestamp}</span>
                      {moment.connected && (
                        <div className="flex items-center gap-1">
                          <Zap className="h-3 w-3 text-yellow-400" />
                          <span className="text-xs text-yellow-400">
                            Connected impact
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* The Virtuous Cycle */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">The Mastery Cycle</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
              <span className="text-green-400 text-sm">1</span>
            </div>
            <div className="flex-1">
              <p className="text-white text-sm">Guest feels special</p>
              <div className="h-1 bg-green-500/20 rounded-full mt-2">
                <div className="h-1 bg-green-500 rounded-full" style={{width: '90%'}} />
              </div>
            </div>
            <ArrowDownRight className="h-4 w-4 text-gray-500" />
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
              <span className="text-blue-400 text-sm">2</span>
            </div>
            <div className="flex-1">
              <p className="text-white text-sm">Team feels proud</p>
              <div className="h-1 bg-blue-500/20 rounded-full mt-2">
                <div className="h-1 bg-blue-500 rounded-full" style={{width: '85%'}} />
              </div>
            </div>
            <ArrowDownRight className="h-4 w-4 text-gray-500" />
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
              <span className="text-purple-400 text-sm">3</span>
            </div>
            <div className="flex-1">
              <p className="text-white text-sm">Owner sees growth</p>
              <div className="h-1 bg-purple-500/20 rounded-full mt-2">
                <div className="h-1 bg-purple-500 rounded-full" style={{width: '78%'}} />
              </div>
            </div>
            <ArrowDownRight className="h-4 w-4 text-gray-500" />
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <span className="text-yellow-400 text-sm">4</span>
            </div>
            <div className="flex-1">
              <p className="text-white text-sm">Investment in excellence</p>
              <div className="h-1 bg-yellow-500/20 rounded-full mt-2">
                <div className="h-1 bg-yellow-500 rounded-full" style={{width: '92%'}} />
              </div>
            </div>
            <ArrowUpRight className="h-4 w-4 text-green-400" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderOwnerView = () => (
    <div className="space-y-6">
      {/* Owner's Command Center */}
      <div className="bg-gradient-to-br from-purple-900 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">Strategic Mastery</h2>
            <p className="text-purple-200">Your vision is becoming reality</p>
          </div>
          <Crown className="h-10 w-10 text-yellow-400" />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <Coins className="h-5 w-5" />
              <span className="text-xs text-green-300">+23%</span>
            </div>
            <p className="text-2xl font-bold">€127.3k</p>
            <p className="text-xs text-purple-200">Monthly Revenue</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <Star className="h-5 w-5" />
              <span className="text-xs text-green-300">+0.3</span>
            </div>
            <p className="text-2xl font-bold">4.8</p>
            <p className="text-xs text-purple-200">Guest Rating</p>
          </div>
        </div>

        {/* Strategic Goals */}
        <div className="space-y-2">
          {ownerGoals.map((goal, index) => {
            const Icon = goal.icon;
            return (
              <div key={index} className="bg-white/10 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="text-sm">{goal.name}</span>
                  </div>
                  <span className="text-xs text-purple-200">{goal.target}</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full">
                  <div 
                    className="h-2 bg-gradient-to-r from-yellow-400 to-green-400 rounded-full"
                    style={{width: `${goal.progress}%`}}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Investment Opportunities */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Brain className="h-5 w-5 text-yellow-400" />
          AI Recommendations
        </h3>
        <div className="space-y-3">
          <div className="bg-gray-700/50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">Install Solar Panels</p>
                <p className="text-gray-400 text-xs">ROI: 18 months • Savings: €2k/month</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">Add Wellness Program</p>
                <p className="text-gray-400 text-xs">Revenue: +€15k/month • Demand: High</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeamView = () => (
    <div className="space-y-6">
      {/* Team Empowerment Hub */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">Team Mastery</h2>
            <p className="text-blue-200">You're making the difference</p>
          </div>
          <Trophy className="h-10 w-10 text-yellow-400" />
        </div>

        {/* Personal Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
            <HandHeart className="h-5 w-5 mb-2" />
            <p className="text-2xl font-bold">47</p>
            <p className="text-xs text-blue-200">Guests Delighted</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
            <Flame className="h-5 w-5 mb-2" />
            <p className="text-2xl font-bold">12</p>
            <p className="text-xs text-blue-200">Day Streak</p>
          </div>
        </div>

        {/* Team Challenges */}
        <div className="space-y-2">
          {teamChallenges.map((challenge, index) => {
            const Icon = challenge.icon;
            return (
              <div key={index} className="bg-white/10 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="text-sm">{challenge.name}</span>
                  </div>
                  {challenge.completed ? (
                    <CheckCircle2 className="h-4 w-4 text-green-400" />
                  ) : (
                    <span className="text-xs text-blue-200">
                      {challenge.progress}/{challenge.target}
                    </span>
                  )}
                </div>
                <div className="text-xs text-blue-300 mt-1">
                  Reward: {challenge.reward}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recognition Wall */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Star className="h-5 w-5 text-yellow-400" />
          Recognition Wall
        </h3>
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg p-3 border border-yellow-500/30">
            <p className="text-white text-sm font-medium">João - Hero of the Day!</p>
            <p className="text-gray-400 text-xs mt-1">Fixed AC in record time, saved 3 bookings</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-white text-sm">Maria earned 'Guest Whisperer' badge</p>
            <p className="text-gray-400 text-xs mt-1">10 five-star mentions this month</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGuestView = () => (
    <div className="space-y-6">
      {/* Guest's Discovery Journey */}
      <div className="bg-gradient-to-br from-green-900 to-green-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">Your Journey</h2>
            <p className="text-green-200">Discovering paradise, one moment at a time</p>
          </div>
          <Compass className="h-10 w-10 text-yellow-400" />
        </div>

        {/* Journey Progress */}
        <div className="space-y-3">
          {guestJourney.map((stage, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                stage.mastered ? 'bg-green-400' : 'bg-white/20'
              }`}>
                {stage.mastered ? (
                  <CheckCircle2 className="h-5 w-5 text-white" />
                ) : (
                  <Circle className="h-5 w-5 text-white/50" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{stage.stage}</p>
                <p className="text-xs text-green-200">{stage.delight}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Today's Magic */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-yellow-400" />
          Today's Magic Moments
        </h3>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/room')}
            className="w-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg p-3 text-left hover:from-purple-500/30 hover:to-blue-500/30 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">Your Room Awaits</p>
                <p className="text-gray-400 text-xs">Everything set to perfection</p>
              </div>
              <Home className="h-5 w-5 text-purple-400" />
            </div>
          </button>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">Sunset Kayaking</p>
                <p className="text-gray-400 text-xs">Starts in 2 hours • 2 spots left</p>
              </div>
              <Lock className="h-5 w-5 text-gray-500" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 via-blue-900 to-green-900 px-4 py-6">
        <h1 className="text-2xl font-bold text-white text-center mb-2">
          Pousada Mastery
        </h1>
        <p className="text-center text-white/80 text-sm">
          Where everyone achieves excellence together
        </p>
      </div>

      {/* View Selector */}
      <div className="px-4 py-4">
        <div className="bg-gray-800 rounded-xl p-1 flex">
          {(['unified', 'owner', 'team', 'guest'] as const).map((view) => (
            <button
              key={view}
              onClick={() => setSelectedView(view)}
              className={`flex-1 py-2 px-3 rounded-lg font-medium capitalize transition-all ${
                selectedView === view
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {view === 'unified' && <Sparkles className="h-4 w-4 inline mr-1" />}
              {view === 'owner' && <Crown className="h-4 w-4 inline mr-1" />}
              {view === 'team' && <Users className="h-4 w-4 inline mr-1" />}
              {view === 'guest' && <Heart className="h-4 w-4 inline mr-1" />}
              {view}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4">
        {selectedView === 'unified' && renderUnifiedView()}
        {selectedView === 'owner' && renderOwnerView()}
        {selectedView === 'team' && renderTeamView()}
        {selectedView === 'guest' && renderGuestView()}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700">
        <div className="flex justify-around py-2">
          <button 
            onClick={() => navigate('/operator-mastery')}
            className="flex flex-col items-center gap-1 py-2 px-4 text-purple-400"
          >
            <BarChart3 className="h-5 w-5" />
            <span className="text-xs">Operations</span>
          </button>
          <button 
            onClick={() => navigate('/room')}
            className="flex flex-col items-center gap-1 py-2 px-4 text-blue-400"
          >
            <Home className="h-5 w-5" />
            <span className="text-xs">Room</span>
          </button>
          <button 
            onClick={() => navigate('/mastery')}
            className="flex flex-col items-center gap-1 py-2 px-4 text-green-400"
          >
            <Compass className="h-5 w-5" />
            <span className="text-xs">Explore</span>
          </button>
          <button className="flex flex-col items-center gap-1 py-2 px-4 text-gray-400">
            <MessageSquare className="h-5 w-5" />
            <span className="text-xs">Chat</span>
          </button>
        </div>
      </div>
    </div>
  );
}